import { getBooking } from "../../../app/api/getBooking";
import { markForVoucherPayment, validateVoucherByVoucherCode } from "../../../app/api/manageVoucher";
import { BaseAction } from "../../../core/actions/BaseAction";
import { calculatePrice, isEmpty } from "../../../Lib/Utils/validationUtil";
import { toVoucherErrorKey } from "../../../Lib/Utils/voucherErrors";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IBookingResponse } from "../../../Orchestrator/Types/Index";
import { IValidateVoucherCodeActionParams } from "../../../Orchestrator/Types/Interface/ActionParams/VoucherActionParams";
import { IPendingVouchers, IValidateVoucherResponse, IVoucherMarkForPaymentRequest } from "../../../Orchestrator/Types/Interface/IVoucher";
import { prepareBookingSummaryAction } from "../Checkout/Summary/PrepareBookingSummaryAction";

export class ValidateVoucherCodeAction extends BaseAction<string, IValidateVoucherCodeActionParams> {
    private toVoucherPaidAmount(value: number): { amount: number; scale: number } {
        const normalizedValue = Number.isFinite(value) ? Math.max(0, value) : 0;
        const [wholePart = "0", fractionPartRaw = ""] = normalizedValue.toString().split(".");
        const sanitizedWholePart = wholePart.replace(/\D/g, "") || "0";
        const fractionPart = (fractionPartRaw.replace(/\D/g, "") + "00").slice(0, 2);
        const amount = Number.parseInt(`${sanitizedWholePart}${fractionPart}`, 10) || 0;

        return { amount, scale: 2 };
    }

    async validateVoucher(bookingId: string, voucherCode: string, billingAmount: number) {
        let actionResponse: string = ""
        try {
            if (!isEmpty(voucherCode)) {
                const response: any = await validateVoucherByVoucherCode(bookingId, voucherCode);
                if (response.status === "ok" && response.answer) {
                    const validateVoucher: IValidateVoucherResponse = response.answer;
                    if (validateVoucher.voucherRemainingValue) {
                        const amountToMarkForPayment = Math.min(billingAmount, validateVoucher.voucherRemainingValue);
                        const normalizedPaidAmount = this.toVoucherPaidAmount(amountToMarkForPayment);
                        const payload: IVoucherMarkForPaymentRequest = {
                            paidAmount: {
                                currency: "AED",
                                amount: normalizedPaidAmount.amount,
                                scale: normalizedPaidAmount.scale
                            },
                            offerPartIds: []
                        }
                        const markPayment: any = await markForVoucherPayment(bookingId, voucherCode, payload)
                        if (markPayment.status === "ok") {
                            actionResponse = "success"
                            // Clear applied voucher in input box
                            updateRecoilState("voucherCodeAtom", "")
                            const bookingResponse: any = await getBooking(bookingId);
                            if (bookingResponse.status === "ok" && bookingResponse.answer) {
                                updateRecoilState("bookingResponseAtom", bookingResponse.answer)

                                const { booking }: IBookingResponse = bookingResponse.answer
                                const isOnHoldBooking = (booking?.bookedOffers || []).some(offer =>
                                    (offer?.admissions || []).some(admission => admission?.status === "ON_HOLD")
                                )

                                prepareBookingSummaryAction.setParams({
                                    ...booking,
                                    ...(isOnHoldBooking ? { isFrom: "on-hold-booking" as const } : {})
                                })
                                await prepareBookingSummaryAction.execute()

                                const calculateVoucherAmount: IPendingVouchers[] = booking.pendingVouchers.map(v => {
                                    return {
                                        ...v,
                                        amount: {
                                            ...v.amount,
                                            amount: calculatePrice({ amount: v.amount.amount, scale: v.amount.scale, currency: "", vats: [] })
                                        }
                                    }
                                })
                                updateRecoilState("listOfAppliedVoucherAtom", calculateVoucherAmount)
                            }
                        } else {
                            // Unknown codes degrade to "invalid voucher code".
                            updateRecoilState("voucherErrorAtom", toVoucherErrorKey(markPayment.errorCode))
                        }
                    }
                } else {
                    updateRecoilState("voucherErrorAtom", toVoucherErrorKey(response.errorCode))
                }
            }
            else {
                updateRecoilState("voucherErrorAtom", "voucherCodeRequired");
            }
        } catch (error: any) {
            console.error("Error fetching widgets", error);
        } finally {
            return actionResponse
        }
    }
    override async run(): Promise<string> {
        let actionResponse = ""
        try {
            const { bookingId, voucherCode, billingAmount } = this.params!
            if (bookingId) {
                actionResponse = await this.validateVoucher(bookingId, voucherCode, billingAmount)
                if (actionResponse === "success") {
                    updateRecoilState("voucherErrorAtom", "");
                }
            }
        } catch (error) {
            console.error("Error during my account action menu click:", error);
        } finally {
            return actionResponse
        }
    }
}

export const validateVoucherCodeAction = new ValidateVoucherCodeAction();
